import axios from 'src/utils/axios';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CertificationSummary {
  id: number;
  property_name: string;
  unit_label: string;
  cert_type_label: string;
  effective_date: string | null;
  submitted_at: string | null;
  answer_count: number;
  status_display: string;
  is_active: boolean;
  is_done: boolean;
  is_terminal: boolean;
  enable_ai_agent: boolean;
  renter_active: boolean;
  log_number: string | null;
  household_name: string;
  household_size: number;
  survey_id: number | null;
  needs_action: boolean;
}

export interface SnapshotFile {
  id: number;
  filename: string;
  file_type: 'pdf' | 'image' | 'other';
  size_bytes: number | null;
  download_url: string;
  is_stale: boolean;
}

export interface SnapshotAnswer {
  id: number;
  qit_id: number;
  question_label: string;
  display_value: string;
  display_type: string;
  raw_value: null; // always null — AC-CC-09
  last_updated: string | null;
  files: SnapshotFile[];
}

export interface SnapshotGroup {
  id: number;
  name: string;
  order: number;
  answers: SnapshotAnswer[];
}

export interface SnapshotPage {
  id: number;
  name: string;
  order: number;
  groups: SnapshotGroup[];
}

export interface SnapshotSection {
  id: number;
  name: string;
  order: number;
  pages: SnapshotPage[];
}

export interface CertificationSnapshotResponse {
  certification: CertificationSummary;
  sections: SnapshotSection[];
  rendered_at: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface DVOpts {
  actingOnMemberId?: number | null;
}

const basePath = (opts: DVOpts): string =>
  opts.actingOnMemberId
    ? `data-vault/members/${opts.actingOnMemberId}/history`
    : `data-vault/history`;

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export const getCertifications = async (
  opts: DVOpts = {}
): Promise<CertificationSummary[]> => {
  const { data } = await axios.get<
    CertificationSummary[] | { results: CertificationSummary[] }
  >(`${basePath(opts)}/certifications/`);
  // DRF with pagination_class=None returns a plain list
  return Array.isArray(data)
    ? data
    : (data as { results: CertificationSummary[] }).results ?? [];
};

export const getCertificationSnapshot = async (
  certificationId: number,
  opts: DVOpts = {}
): Promise<CertificationSnapshotResponse> => {
  const { data } = await axios.get<CertificationSnapshotResponse>(
    `${basePath(opts)}/certifications/${certificationId}/answers/`
  );
  return data;
};
